/**
 * FAQ datasets for AEO (Answer Engine Optimisation).
 *
 * Each export is an array of { question, answer } objects designed for the
 * <FAQSection /> component. Answers are 60-200 chars, factually anchored,
 * written in Australian English, third person (no "we") so AI engines
 * extract them as authoritative statements about Educ4te.
 */

export const HOMEPAGE_FAQS = [
  {
    question: 'What does Educ4te do?',
    answer:
      'Educ4te is an Australian cybersecurity and Microsoft 365 training organisation specialising in ACSC Essential Eight alignment, Copilot governance, and security assessments for mid-market and enterprise IT teams.',
  },
  {
    question: 'Who runs Educ4te?',
    answer:
      'Educ4te is led by Alpesh Nakar, a Microsoft Most Valuable Professional (MVP) with over two decades of experience in Microsoft 365 security, governance, and Australian regulatory compliance.',
  },
  {
    question: "Is Educ4te's training Microsoft-aligned?",
    answer:
      'Yes. Courses map to current Microsoft Learn role-based certifications (SC-200, SC-300, SC-400, MS-500, AZ-500) and the official Microsoft 365 Copilot adoption framework.',
  },
  {
    question: "What's the Essential Eight and why does it matter?",
    answer:
      'The Essential Eight is the Australian Cyber Security Centre (ACSC) baseline of eight mitigation strategies. It is mandatory for Commonwealth non-corporate entities and the recognised baseline for Australian businesses handling sensitive data.',
  },
  {
    question: 'Does Educ4te offer Copilot governance training?',
    answer:
      'Yes. Educ4te delivers dedicated programmes covering Microsoft 365 Copilot, Copilot Studio, and Agent 365 — including SharePoint oversharing remediation, Purview labelling, and Essential Eight-aligned activation roadmaps.',
  },
];

export const COPILOT_SECURITY_ROADMAP_FAQS = [
  {
    question: 'What does the 90-day Copilot security roadmap cover?',
    answer:
      'The roadmap maps all eight ACSC Essential Eight controls to Microsoft 365 Copilot Wave 3, sequencing identity hardening, data governance, agentic AI controls, and IRAP audit evidence into a 90-day activation plan.',
  },
  {
    question: 'Is Microsoft 365 Copilot IRAP-Protected in Australia?',
    answer:
      'Yes. Microsoft 365 Copilot completed an Australian IRAP assessment to the Protected classification level in 2024, covering the Australian East and Australian Southeast Azure regions.',
  },
  {
    question: 'What Essential Eight controls apply to Copilot?',
    answer:
      'All eight apply, with the highest impact on application control, restrict administrative privileges, multi-factor authentication, and patching of operating systems and applications hosting Copilot agents.',
  },
  {
    question: 'Can we deploy Copilot without an IRAP assessment?',
    answer:
      'Private-sector organisations do not require their own IRAP assessment. Government entities at Protected classification rely on the existing Microsoft Copilot IRAP-Protected report and complete their own consumer guide attestation.',
  },
];

export const COPILOT_GOVERNANCE_PLAYBOOK_FAQS = [
  {
    question: 'What is Copilot governance?',
    answer:
      'Copilot governance is the set of identity, data, and content controls that determine what Microsoft 365 Copilot can surface. It spans Microsoft Entra licensing, SharePoint permissions, Purview sensitivity labels, and DLP policies.',
  },
  {
    question: 'Does Copilot move Australian data offshore?',
    answer:
      "No, when configured correctly. Microsoft 365 Copilot processes data in the tenant's geo. Australian commercial tenants are pinned to Australia East and Australia Southeast Azure regions for prompts, responses, and grounding data.",
  },
  {
    question: 'What Privacy Act 1988 obligations apply to Copilot?',
    answer:
      'Australian Privacy Principles 1, 5, 6, 8, and 11 apply. Organisations must update privacy notices to disclose AI processing, restrict secondary use, and assess any cross-border disclosure risk before activation.',
  },
  {
    question: 'Is Microsoft Purview required for Copilot?',
    answer:
      'Purview is not licence-mandated but is the recommended control plane. Sensitivity labels, DLP, and Insider Risk Management are the practical mechanisms to limit Copilot grounding to appropriate content.',
  },
];

export const COPILOT_WAVES_FOUNDATION_FAQS = [
  {
    question: 'What are Copilot Waves 1 and 2?',
    answer:
      'Wave 1 (November 2023) introduced Microsoft 365 Copilot for Word, Excel, PowerPoint, Outlook, and Teams. Wave 2 (September 2024) added Copilot Pages, advanced Excel agents, and Copilot Studio agent authoring.',
  },
  {
    question: 'What changed between Wave 1 and Wave 2?',
    answer:
      'Wave 2 introduced agent extensibility via Copilot Studio, the Copilot Pages collaborative canvas, Python in Excel through Copilot, and prioritised the Business Chat (BizChat) experience as the unified interface.',
  },
  {
    question: 'What controls must be in place before activating Copilot?',
    answer:
      'Multi-factor authentication, restrict admin privileges, sensitivity labels published in Purview, SharePoint oversharing remediated, and conditional access policies covering the Microsoft 365 Copilot service principal.',
  },
  {
    question: 'When was Microsoft 365 Copilot released in Australia?',
    answer:
      'Microsoft 365 Copilot became generally available to Australian enterprise customers on 1 November 2023, with small and medium business availability following on 16 January 2024.',
  },
];

export const COPILOT_WAVE_3_FAQS = [
  {
    question: 'What is Copilot Wave 3?',
    answer:
      'Copilot Wave 3 is the agentic phase announced at Microsoft Ignite 2024. It centres on autonomous agents built in Copilot Studio, the Agent 365 management surface, and reasoning-capable Copilot orchestration.',
  },
  {
    question: 'What is Agent 365?',
    answer:
      'Agent 365 is the Microsoft control plane for managing AI agents across an enterprise. Announced at Microsoft Ignite 2024, it provides identity, access, security, and compliance management for agents at scale.',
  },
  {
    question: 'Are agents covered by Essential Eight?',
    answer:
      'Yes. Agents are applications and inherit application control, patching, and admin privilege requirements. ACSC guidance treats autonomous agents as software with elevated identity scopes that require equivalent hardening.',
  },
  {
    question: "What's the difference between Copilot and Copilot Studio?",
    answer:
      'Microsoft 365 Copilot is the licensed end-user assistant. Copilot Studio is the low-code authoring environment for building custom agents, declarative agents, and connectors that extend or replace the default Copilot experience.',
  },
];

export const COPILOT_READINESS_FAQS = [
  {
    question: 'What does the Copilot Readiness Assessment cover?',
    answer:
      'The assessment evaluates identity, licensing, SharePoint permissions hygiene, Purview labelling maturity, Essential Eight posture, and change-management readiness across eight scored domains.',
  },
  {
    question: 'How long does the assessment take?',
    answer:
      'The self-serve questionnaire takes approximately 15 to 20 minutes to complete and produces an instant scored summary across the eight Copilot readiness domains.',
  },
  {
    question: 'Will I get a written report?',
    answer:
      'Yes. Respondents receive an emailed PDF report scoring each domain from 0 to 5, identifying gaps against Essential Eight Maturity Level 2 and Microsoft 365 Copilot prerequisites.',
  },
  {
    question: 'Who scores my assessment?',
    answer:
      'Responses are scored against a published rubric maintained by Educ4te, aligned to the ACSC Essential Eight Maturity Model and the Microsoft 365 Copilot adoption framework.',
  },
  {
    question: 'Is the assessment free?',
    answer:
      'Yes. The Copilot Readiness Assessment is provided free of charge. A paid consultative debrief is available optionally for organisations that want guided remediation planning.',
  },
];

export const ESSENTIAL_EIGHT_FAQS = [
  {
    question: 'What is the ACSC Essential Eight?',
    answer:
      'The Essential Eight is a baseline of eight mitigation strategies published by the Australian Cyber Security Centre (ACSC), part of the Australian Signals Directorate (ASD), to protect organisations against cyber threats.',
  },
  {
    question: 'What is Maturity Level 2?',
    answer:
      'Maturity Level 2 is the second of four maturity levels (0 to 3) in the ACSC Essential Eight Maturity Model. It targets adversaries with modest tradecraft and is the recommended baseline for most non-government Australian organisations.',
  },
  {
    question: 'Is the Essential Eight mandatory in Australia?',
    answer:
      'It is mandatory for Commonwealth non-corporate entities under the Protective Security Policy Framework (PSPF). For private sector organisations it is the de facto baseline expected by Australian cyber insurers and regulators.',
  },
  {
    question: 'How long does Essential Eight ML2 alignment take?',
    answer:
      'A typical mid-market Microsoft 365 organisation reaches Maturity Level 2 in six to nine months of focused programme work, depending on patching maturity, application control posture, and admin privilege hygiene.',
  },
  {
    question: 'Does the Essential Eight cover AI?',
    answer:
      'The eight controls apply to AI workloads as applications and identities. ASD published supplementary guidance on AI and large language model deployment in 2024 that supplements the core Essential Eight controls.',
  },
];

export const RESOURCES_HUB_FAQS = [
  {
    question: "What's in the Educ4te resource library?",
    answer:
      'The library contains free PDF playbooks, checklists, and reference cards covering Microsoft 365 Copilot governance, ACSC Essential Eight alignment, SharePoint oversharing remediation, and Australian privacy obligations.',
  },
  {
    question: 'Are the resources free?',
    answer:
      'All current resources in the public library are free. Premium gated resources require email verification and may require account sign-in for ongoing access.',
  },
  {
    question: 'How do I get the download link?',
    answer:
      'Submit an email address on the resource page and Educ4te issues a one-time signed download link valid for 15 minutes. No account creation is required for free public resources.',
  },
  {
    question: 'Can I share the resources?',
    answer:
      'Yes, the public PDF resources may be shared internally within an organisation. Each download link is single-use, but the underlying PDFs carry no per-recipient restrictions.',
  },
];

export const SHAREPOINT_GOVERNANCE_FAQS = [
  {
    question: 'What is SharePoint and Teams governance?',
    answer:
      'SharePoint and Teams governance is the set of controls — site lifecycle, sharing policies, sensitivity labels, and access reviews — that keep Microsoft 365 content from being overshared, especially before enabling Microsoft 365 Copilot.',
  },
  {
    question: 'Why does SharePoint oversharing matter for Copilot?',
    answer:
      'Microsoft 365 Copilot surfaces any content a user can already access. Oversharing — broad "Everyone" links and open sites — means Copilot can expose sensitive data, so remediating permissions is a prerequisite for safe deployment.',
  },
  {
    question: 'What controls reduce SharePoint oversharing?',
    answer:
      'Key controls include SharePoint Advanced Management data access governance reports, Restricted Content Discovery, sensitivity labels with default site labelling, site access reviews, expiring sharing links, and Purview data classification to find and protect exposed content.',
  },
  {
    question: 'How does the SharePoint & Teams Governance assessment work?',
    answer:
      'The free 15-question self-assessment scores governance maturity across site lifecycle, sharing controls, and information protection, returning an instant traffic-light scorecard with specific Microsoft 365 admin centre actions.',
  },
  {
    question: 'Is the assessment aligned to Australian frameworks?',
    answer:
      'Yes. Recommendations reference the ACSC Essential Eight, the Australian Privacy Principles under the Privacy Act 1988, and Microsoft 365 admin guidance relevant to Australian organisations.',
  },
];
